import Box from '@mui/material/Box';
import List from '@mui/material/List';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import ActivityIcon from '@/assets/icons/activity.svg?react';
import Logo from '@/assets/icons/caliza.svg?react';
import ContactsIcon from '@/assets/icons/contacts.svg?react';
import HomeIcon from '@/assets/icons/home.svg?react';
import CustomersIcon from '@/assets/icons/sender.svg?react';
import SettingsIcon from '@/assets/icons/settings.svg?react';
import SignoutIcon from '@/assets/icons/sign-out.svg?react';
import { MainMenuItem } from '@/components/molecules/MainMenuItem';
import { FF_DASHBOARD_CUSTOMER_MANAGEMENT } from '@/data/featureFlags';
import { useIntegratorFeatureFlag } from '@/hooks/useFeatureFlags';
import { usePermissionsManager } from '@/hooks/usePermissionsManager';
import { useTranslator } from '@/hooks/useTranslator';
import { Permission } from '@/types/permissions';

interface MenuItem {
  readonly label: string;
  readonly path: string;
  readonly icon: React.ReactElement;
  readonly skip: boolean;
}

export const ApplicationMenu: React.FC = (): React.ReactElement => {
  const { userIsAuthorizedTo } = usePermissionsManager();

  const tr = useTranslator();
  const customerManagementEnabled = useIntegratorFeatureFlag(
    FF_DASHBOARD_CUSTOMER_MANAGEMENT,
    false,
  );

  const menu = React.useMemo(
    (): readonly MenuItem[] =>
      [
        {
          label: tr('HOME'),
          path: '../home',
          icon: <HomeIcon height={20} fill="currentColor" />,
          skip: false,
        },
        {
          label: tr('ACTIVITY'),
          path: '../activity',
          icon: <ActivityIcon height={24} fill="currentColor" />,
          skip: !userIsAuthorizedTo(Permission.VIEW_ACTIVITY),
        },
        {
          label: tr('CONTACTS'),
          path: '../contacts',
          icon: <ContactsIcon height={20} fill="currentColor" />,
          skip: !userIsAuthorizedTo(Permission.MANAGE_PAYMENT_CONTACTS),
        },
        {
          label: tr('CUSTOMERS'),
          path: '../customers',
          icon: <CustomersIcon height={20} fill="currentColor" />,
          skip: !customerManagementEnabled || !userIsAuthorizedTo(Permission.MANAGE_CUSTOMER),
        },
      ].filter((item: MenuItem): boolean => !item.skip),
    [tr, userIsAuthorizedTo, customerManagementEnabled],
  );

  const footer = React.useMemo(
    (): readonly MenuItem[] =>
      [
        {
          label: tr('SETTINGS'),
          path: '../settings',
          icon: <SettingsIcon height={20} fill="currentColor" />,
          skip: false,
        },
        {
          label: tr('SIGN_OUT'),
          path: 'sign-out',
          icon: <SignoutIcon height={20} fill="currentColor" />,
          skip: false,
        },
      ].filter((item: MenuItem): boolean => !item.skip),
    [tr],
  );

  return (
    <Box sx={styles.container}>
      <Box sx={styles.logo}>
        <Logo role="img" aria-label="Caliza" height="100%" fill="currentColor" />
      </Box>
      <List sx={styles.list} disablePadding={true}>
        {menu.map((item: MenuItem): React.ReactElement => {
          return (
            <MainMenuItem key={item.label} label={item.label} path={item.path} icon={item.icon} />
          );
        })}
      </List>
      <Box flex={1} /* Push down the footer menu */ />
      <Box position="relative" height="1px" mb={5} bgcolor="custom.DividerMed" />
      <List sx={styles.list} disablePadding={true}>
        {footer.map(
          (item: MenuItem): React.ReactElement => (
            <MainMenuItem key={item.label} label={item.label} path={item.path} icon={item.icon} />
          ),
        )}
      </List>
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    px: 3,
    py: 6,
  },
  logo: {
    color: 'primary.dark',
    px: 2,
    height: 26,
    mb: 4,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
};
